import React, { useState, useEffect, useCallback } from 'react';
import { getChatList, ChatSession, getChatDisplayMessages } from '../services/tauri-api';
import SettingsModal from './SettingsModal';
import { extractLastUserMessageSync } from '../utils/extractLastUserMessage';
import { getSessionChatIds, SESSION_CHAT_IDS_EVENT } from '../utils/sessionStorage';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  showAllSiblings?: boolean;
  onToggleShowAll?: (value: boolean) => void;
  onChatSelect?: (chatId: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  isOpen, 
  onClose, 
  showAllSiblings = false, 
  onToggleShowAll,
  onChatSelect
}) => {
  const [chats, setChats] = useState<ChatSession[]>([]);
  const [chatPreviews, setChatPreviews] = useState<Record<string, string>>({});
  const [globalBusy, setGlobalBusy] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState<'general' | 'chats' | 'about'>('general');

  // チャット一覧を読み込み
  const loadChats = useCallback(async () => {
    try {
      const response = await getChatList();
      if (response.success && response.data) {
        // セッションストレージに保存されたチャットIDのみをフィルタリング
        const sessionChatIds = getSessionChatIds();
        const filteredChats = response.data.filter(chat => 
          sessionChatIds.includes(chat.chat_id)
        );
        
        // created_at でソート（新しい順）
        const sortedChats = filteredChats.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setChats(sortedChats);
        
        // 各チャットのプレビューを取得
        const previews: Record<string, string> = {};
        for (const chat of sortedChats) {
          try {
            const displayResponse = await getChatDisplayMessages(chat.chat_id);
            if (displayResponse.success && displayResponse.data && displayResponse.data.length > 0) {
              const firstMessage = displayResponse.data[0];
              // ユーザーメッセージの場合、最後の <|start_header_id|>user<|end_header_id|> と <|eot_id|> の間の内容を抽出
              const content = firstMessage.role === 'human'
                ? extractLastUserMessageSync(firstMessage.content)
                : firstMessage.content;
              const preview = content && content.length > 0
                ? (content.length > 50 
                    ? content.substring(0, 50) + '...'
                    : content)
                : 'メッセージなし';
              previews[chat.chat_id] = preview;
            } else {
              previews[chat.chat_id] = 'メッセージなし';
            }
          } catch (error) {
            console.error(`チャット ${chat.chat_id} のプレビュー取得に失敗:`, error);
            previews[chat.chat_id] = 'メッセージなし';
          }
        }
        setChatPreviews(previews);
      }
    } catch (error) {
      console.error('チャット一覧の読み込みに失敗:', error);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadChats();
    }
  }, [isOpen, loadChats]);

  // 大画面表示時などサイドバーが常時表示でも初回に履歴を読み込む
  useEffect(() => {
    loadChats();
  }, [loadChats]);

  useEffect(() => {
    const handleSessionChatsChanged = () => {
      loadChats();
    };
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener(SESSION_CHAT_IDS_EVENT, handleSessionChatsChanged);
      return () => {
        window.removeEventListener(SESSION_CHAT_IDS_EVENT, handleSessionChatsChanged);
      };
    }
    return;
  }, [loadChats]);

  // ヘッダーからの「開発について」アクションで設定モーダルのアプリ情報タブを開く
  useEffect(() => {
    const handleOpenSettingsAbout = () => {
      setSettingsInitialTab('about');
      setIsSettingsOpen(true);
    };
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('open-settings-about', handleOpenSettingsAbout);
      return () => {
        window.removeEventListener('open-settings-about', handleOpenSettingsAbout);
      };
    }
    return;
  }, []);

  // グローバルBusyフラグをポーリングで反映（簡易）
  useEffect(() => {
    const timer = setInterval(() => {
      const busy = typeof window !== 'undefined' && (window as any).__FLOW_BUSY__ === true;
      setGlobalBusy(busy);
    }, 300);
    return () => clearInterval(timer);
  }, []);

  // チャットのプレビューを取得（プレビュー用）
  const getChatPreview = (chat: ChatSession): string => {
    if (chat.messages.length === 0) return 'メッセージなし';
    return chatPreviews[chat.chat_id] || '読み込み中...';
  };

  return (
    <>
      {/* オーバーレイ */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* サイドバー */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">LLMView</h2>
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            aria-label="サイドバーを閉じる"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <nav className="px-4 py-6 space-y-6">
          {/* ナビゲーション（チャット履歴と設定） */}
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">ナビゲーション</h3>
            <ul className="space-y-1">
              <li>
                <div className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-gray-700">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                  <span className="font-medium">チャット履歴</span>
                </div>
                {/* チャット一覧（最新順） */}
                <div className="mt-1 max-h-96 overflow-y-auto space-y-1 px-3">
                  {chats.map(chat => (
                    <div 
                      key={chat.chat_id} 
                      className="text-xs text-gray-600 hover:bg-gray-50 p-2 rounded cursor-pointer transition-colors border border-transparent hover:border-gray-200"
                      onClick={() => onChatSelect && onChatSelect(chat.chat_id)}
                    >
                      <div className="font-medium text-gray-800 mb-1">{getChatPreview(chat)}</div>
                      <div className="text-gray-400 text-xs">
                        {chat.messages.length} メッセージ • {new Date(chat.created_at).toLocaleString('ja-JP')}
                      </div>
                    </div>
                  ))}
                  {chats.length === 0 && (
                    <div className="text-xs text-gray-400">チャットなし</div>
                  )}
                </div>
              </li>
              <li>
                <button 
                  onClick={() => setIsSettingsOpen(true)}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-gray-700 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 0 2l-.15.08a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.38a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1 0-2l.15-.08a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path><circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <span className="font-medium">設定</span>
                </button>
              </li>
            </ul>
          </div>

          {/* 表示切替（主要なもののみ表示） */}
          {showAllSiblings !== undefined && (
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">表示</h3>
              <label className="mt-1 flex items-center justify-between cursor-pointer p-2 rounded-lg hover:bg-gray-100">
                <span className="text-sm font-medium text-gray-700">主要なもののみ表示</span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={!showAllSiblings}
                    onChange={(e) => onToggleShowAll && onToggleShowAll(!e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-blue-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </div>
              </label>
            </div>
          )}
        </nav>
      </div>

      {/* 設定モーダル */}
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        globalBusy={globalBusy}
        initialTab={settingsInitialTab}
        onChatDeleted={() => {
          // チャット削除後にチャット一覧をリロード
          loadChats();
        }}
      />
    </>
  );
};

export default Sidebar;
